/**
 * Modèle de contrat d’affiliation (FR) — à valider avec un juriste avant production.
 * Placeholders : compléter ou remplacer selon votre société et votre politique commerciale.
 */
export const AFFILIATE_CONTRACT_DRAFT_FR = `## Contrat de partenariat / programme d’affiliation

**Entre les soussignés :**

- **{{EDITEUR_RAISON_SOCIALE}}**, {{FORME_JURIDIQUE}} au capital de {{CAPITAL_SOCIAL}}, dont le siège social est situé {{SIEGE_SOCIAL_ADRESSE}}, immatriculée au RCS de {{RCS_VILLE}} sous le numéro {{NUMERO_RCS}}, SIREN {{SIREN}}, TVA intracommunautaire {{TVA_INTRACOMMUNAUTAIRE}}  
  (« **l’Éditeur** » ou « **Totemium** »),

et

- **Le Partenaire**, personne physique ou morale disposant d’un compte utilisateur sur la plateforme Totemium et ayant activé / demandé l’adhésion au programme d’affiliation  
  (« **le Partenaire** »),

il a été convenu ce qui suit.

---

### 1. Objet

Le présent contrat a pour objet de définir les conditions dans lesquelles le Partenaire peut recommander la plateforme **Totemium** (solution {{DESCRIPTION_PRODUIT_COURTE}}) et percevoir, le cas échéant, une rémunération sous forme de commissions, conformément au paramétrage du programme tel qu’affiché dans l’espace affilié et aux règles techniques décrites ci-dessous.

---

### 2. Adhésion et révocabilité

2.1. L’adhésion au programme est **facultative** et suppose l’acceptation des présentes conditions, ainsi que la version **{{VERSION_CONTRAT}}** enregistrée au moment de l’acceptation.

2.2. L’Éditeur peut **suspendre ou retirer** l’accès au programme (ou à certains canaux de promotion) en cas de manquement aux présentes règles, aux lois applicables, ou aux règles des places de marché / réseaux publicitaires utilisés par le Partenaire.

---

### 3. Moyens de tracking et attribution

3.1. L’attribution d’un « Filleul » (nouveau client payant éligible) repose sur les mécanismes fournis par la plateforme : **lien de parrainage**, **code**, et le cas échéant **cookie first-touch** nommé \`{{NOM_COOKIE_REF}}\` pour une durée maximale de **{{DUREE_COOKIE_JOURS}}** jours (sous réserve des réglages effectifs dans l’administration).

3.2. Le Partenaire reconnaît que l’attribution est **automatique** et dépend des informations transmises à l’inscription et/ou au paiement ; l’Éditeur ne garantit pas un résultat commercial minimal.

---

### 4. Commissions — principe

4.1. Sous réserve d’éligibilité, une commission peut être due sur la base **{{BASE_COMMISSION}}** (ex. : montant encaissé sur facture, hors taxes ou toutes taxes comprises selon votre politique — **à préciser**), au taux de **{{TAUX_COMMISSION}}** (ex. : **{{TAUX_BPS}}** basis points, soit **{{TAUX_POURCENT}} %**), avec les exceptions suivantes : plans ou offres exclus (**{{PLANS_EXCLUS}}**), montants minimums (**{{MONTANT_MIN_FACTURE}}** centimes / devise **{{DEVISE_LEDGER}}**), et paramètres de **récurrence** (premier paiement uniquement vs renouvellements — **{{REGLE_RECURRENCE}}**).

4.2. Les commissions sont **indicatives** tant qu’elles ne sont pas validées par la comptabilité ; elles peuvent être **ajustées ou annulées** en cas de remboursement, litige, fraude, erreur de facturation ou impayé (mécanisme de **clawback**).

4.3. Un **délai de carence** peut s’appliquer : les commissions deviennent « payables » au plus tôt **{{DELAI_APRES_ATTRIBUTION_JOURS}}** jours après la date d’attribution du Filleul (paramètre « payable après enrôlement »).

---

### 5. Versements

5.1. Les modalités de paiement (virement, plateforme, **Stripe Connect**, etc.) sont celles décrites dans l’espace affilié au moment du versement. Le Partenaire fournit les informations bancaires / fiscales **exactes** et se conforme aux obligations de **facturation** ou d’auto-facturation selon le cas.

5.2. Les versements peuvent être soumis à un **seuil minimum**, à des **frais** éventuels des intermédiaires de paiement, et aux **retenues légales** applicables.

---

### 6. Obligations du Partenaire

Le Partenaire s’engage notamment à :

- ne pas utiliser de **pratiques trompeuses** (tarifs, fonctionnalités, identité de marque) ;
- respecter la réglementation applicable en matière de **publicité**, d’**influence** et de **comparaisons** ;
- éviter le **spam**, l’**usurpation de marque**, le **brand bidding** abusif sur la marque Totemium sans accord écrit ;
- ne pas créer de confusion sur le caractère officiel ou sponsorisé des contenus ;
- ne pas se **parrainer lui-même** ni organiser de structures artificielles pour contourner les règles ;
- respecter la **protection des données** et le règlement applicable (information des personnes, bases légales, etc.).

---

### 7. Propriété intellectuelle et marque

Le Partenaire bénéficie d’un droit non exclusif d’utiliser les **éléments de marque** fournis par l’Éditeur pour la promotion du service, dans le respect de la charte graphique et des instructions communiquées. Toute utilisation non autorisée est interdite.

---

### 8. Données personnelles

Les données traitées (identité, performance du programme, données de paiement) le sont conformément à la **politique de confidentialité** disponible sur **{{URL_POLITIQUE_CONFIDENTIALITE}}** et aux obligations du Partenaire en tant que responsable de traitement ou co-responsable selon les cas.

---

### 9. Responsabilité

Le Partenaire est seul responsable du contenu qu’il publie et des dommages causés par sa mauvaise exécution. L’Éditeur ne saurait être tenu responsable des **pertes indirectes** ou **manque à gagner** du Partenaire.

---

### 10. Durée et résiliation

Le présent contrat prend effet à la date d’acceptation et demeure en vigueur jusqu’à **résiliation** par l’une ou l’autre partie avec un préavis de **{{PREAVIS_JOURS}}** jours (sauf faute grave), ou immédiatement en cas de manquement grave non réparé.

---

### 11. Droit applicable et litiges

11.1. Le présent contrat est régi par le droit **{{DROIT_APPLICABLE}}**.

11.2. En cas de litige, les parties recherchent une solution amiable avant toute action. À défaut, les tribunaux **{{JURIDICTION_COMPETENTE}}** seront seuls compétents, sous réserve des règles d’ordre public.

---

### 12. Contact programme affiliation

**{{EMAIL_CONTACT_AFFILIATION}}** — Site : **{{URL_SITE_OFFICIEL}}**

---

*Document modèle fourni à titre d’information. {{MENTION_JURISTE}}*
`
